import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createEventStoreApi } from '@bloo-team/api-client'
import type { EventStore, Stream } from '@bloo-team/api-client'
import { client, getSession, hasRole, identityFor, isDemoSession } from '@bloo-team/core-shell'
import { storageStore, useStorageOverlay, type StorageOverlay } from './storage-store'

/**
 * Storage data — the capacity rollup over the streams and the event stores
 * (saved datasets). docs/storage-requirements.md is the source; retention
 * lives in `lib/retention.ts`.
 *
 * Event stores are lookup tables built from a DQL result or an uploaded
 * csv/xlsx, referenced by workbooks and enrichment buckets — capture A0
 * (docs/inventory/storage-capture.json, 2026-09-09) proved the "archives"
 * premise wrong. Reads go through `event_store/list` (observed); the writes
 * are CONFIRMED (A1 predelete → delete, A2 upload) and ship live for the
 * engineer role. Demo mode writes the localStorage overlay
 * (`storage-store.ts`) and the reads apply it, so the page has one code path.
 */

export type { EventStore }

export const eventStoreApi = createEventStoreApi(client)

/**
 * Who may write. Legacy gated event stores on Security Engineer; retention
 * is a storage policy and follows the same role. A UX affordance —
 * authorization is the server's (permissions.ts).
 */
export function canWriteStorage(clusterId: string, scopeId: string): boolean {
  const session = getSession()
  if (!session) return false
  return hasRole(identityFor(session, clusterId, scopeId), ['Security Engineer'])
}

/* ----------------------------------------------------------------- rollup */

export interface StorageRollup {
  /** Sum of `size_on_disk`, bytes; null when there are no streams. */
  onDisk: number | null
  /** Sum of `total_len`, bytes; null when there are no streams. */
  logical: number | null
  streams: number
  /** Days, over the streams that carry a retention; null when none does. */
  retentionMin: number | null
  retentionMax: number | null
  /** Streams with `retention === null`. */
  noRetention: number
}

/** The strip's numbers. No percentage: nothing observed gives a ceiling (CB-32). */
export function storageRollup(streams: Stream[]): StorageRollup {
  if (streams.length === 0) {
    return { onDisk: null, logical: null, streams: 0, retentionMin: null, retentionMax: null, noRetention: 0 }
  }
  let onDisk = 0
  let logical = 0
  let noRetention = 0
  let min: number | null = null
  let max: number | null = null
  for (const s of streams) {
    onDisk += s.size_on_disk
    logical += s.total_len
    if (s.retention === null) {
      noRetention++
      continue
    }
    min = min === null ? s.retention : Math.min(min, s.retention)
    max = max === null ? s.retention : Math.max(max, s.retention)
  }
  return { onDisk, logical, streams: streams.length, retentionMin: min, retentionMax: max, noRetention }
}

/* ----------------------------------------------------------- event stores */

const day = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d)

const dql = (name: string, author: string, userId: string, createdAt: number, modifiedBy: string, modifiedAt: number): EventStore => ({
  name, author, createdBy: author, userId, dataSource: 'DQL', sourceKind: 'dql', extension: null, fileName: null,
  createdAt, modifiedBy, modifiedAt, scopeId: 'gotham',
})

const upload = (name: string, author: string, userId: string, extension: 'csv' | 'xlsx', fileName: string, at: number): EventStore => ({
  name, author, createdBy: userId, userId, dataSource: `File Upload by ${author}`, sourceKind: 'upload', extension, fileName,
  createdAt: at, modifiedBy: userId, modifiedAt: at, scopeId: 'gotham',
})

/**
 * Demo event stores, in the captured shape: four DQL results and two
 * uploads (one csv, one xlsx). Names and authors follow the gotham capture;
 * `renee` is the demo tenant's own engineer.
 */
export const DEMO_EVENT_STORES: EventStore[] = [
  dql('greensnow', 'Bloo', 'bloo.io', day(2026, 3, 31), 'Bloo', day(2026, 9, 6)),
  dql('abuseipdbresult', 'DNIF', 'dnif.it', day(2025, 7, 17), 'Bloo', day(2026, 7, 2)),
  dql('VolumeIP', 'DNIF', 'dnif.it', day(2025, 7, 9), 'Bloo', day(2026, 8, 31)),
  dql('CompromisedHost', 'DNIF', 'dnif.it', day(2024, 6, 21), 'DNIF', day(2025, 7, 8)),
  upload('BATMAN', 'renee', 'renee@wayne.corp', 'csv', 'active_device_export_1698923515', day(2023, 11, 2)),
  upload('test', 'renee', 'renee@wayne.corp', 'xlsx', 'issue-and-risk-log-template', day(2023, 11, 8)),
]

/** Deleted rows gone, uploaded rows appended (an upload of a fixture name replaces it). Keyed by `name`. */
export function applyArchiveOverlay(rows: EventStore[], overlay: StorageOverlay): EventStore[] {
  const { archives } = overlay
  const created = Object.values(archives)
    .map((o) => o.created)
    .filter((c): c is EventStore => !!c)
  const createdNames = new Set(created.map((c) => c.name))
  return [...rows.filter((r) => !createdNames.has(r.name)), ...created].filter((r) => !archives[r.name]?.deleted)
}

const storesKey = (clusterId: string, scopeId: string) =>
  ['event-stores', clusterId, scopeId, isDemoSession() ? 'demo' : 'live'] as const

export function useEventStores({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  const overlay = useStorageOverlay(clusterId, scopeId)
  const q = useQuery<EventStore[]>({
    queryKey: storesKey(clusterId, scopeId),
    queryFn: async () => (isDemo ? DEMO_EVENT_STORES : eventStoreApi.list({ clusterId, scopeId })),
    retry: 1,
  })
  const data = useMemo<EventStore[] | undefined>(() => {
    if (!q.data || !isDemo) return q.data
    return applyArchiveOverlay(q.data, overlay)
  }, [q.data, isDemo, overlay])
  return { ...q, data }
}

/* --------------------------------------------------------------- predelete */

export type PredeleteItemKind = 'workbook' | 'enrichment' | 'unknown'

export interface PredeleteItem {
  kind: PredeleteItemKind
  name: string
}

/**
 * What `predelete` reports: the workbooks and enrichment buckets that use
 * the store. `raw` is the backend's `data` verbatim — it goes back as
 * `predelete_data` on delete (A1), so it is never reshaped.
 */
export interface PredeleteImpact {
  items: PredeleteItem[]
  raw: unknown[]
}

const KIND_KEYS = ['kind', 'type', 'category', 'bucket_type', 'source', 'entity']
const NAME_KEYS = ['name', 'title', 'workbook_name', 'bucket_name', 'label', 'id']

const kindOf = (text: string): PredeleteItemKind =>
  /workbook/i.test(text) ? 'workbook' : /enrich/i.test(text) ? 'enrichment' : 'unknown'

/**
 * The predelete item shape is unobserved (every captured store had none), so
 * items are read loosely: a string is its own name, an object's kind comes
 * from any type-like key (else from the whole item), its name from any
 * name-like key (else the item serialised).
 */
export function classifyPredeleteItem(item: unknown): PredeleteItem {
  if (typeof item === 'string') return { kind: kindOf(item), name: item }
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    const o = item as Record<string, unknown>
    const kindText = KIND_KEYS.map((k) => o[k]).find((v): v is string => typeof v === 'string')
    const nameValue = NAME_KEYS.map((k) => o[k]).find((v) => typeof v === 'string' || typeof v === 'number')
    return {
      kind: kindOf(kindText ?? JSON.stringify(o)),
      name: nameValue !== undefined ? String(nameValue) : JSON.stringify(o),
    }
  }
  return { kind: 'unknown', name: String(item) }
}

export const classifyPredeleteItems = (items: unknown[]): PredeleteItem[] => items.map(classifyPredeleteItem)

/**
 * Demo predelete answers, in a plausible server form: two fixtures are
 * referenced (`abuseipdbresult` by a workbook and an enrichment bucket,
 * `VolumeIP` by a workbook); the rest answer `[]`, as the capture did.
 */
export const DEMO_PREDELETE: Record<string, unknown[]> = {
  abuseipdbresult: [
    { type: 'workbook', name: 'Abuse IPDB hits' },
    { type: 'enrichment', name: 'abuseipdb' },
  ],
  VolumeIP: [{ type: 'workbook', name: 'Top talkers by volume' }],
}

/* ----------------------------------------------------------------- writes */

interface Scope {
  clusterId: string
  scopeId: string
}

function useInvalidate(scope: Scope) {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['event-stores', scope.clusterId, scope.scopeId] })
}

/** The demo twin of `predelete`: the fixture's reference list, empty for anything else. */
function demoImpact(scope: Scope, name: string): PredeleteImpact {
  const rows = applyArchiveOverlay(DEMO_EVENT_STORES, storageStore(scope.clusterId, scope.scopeId).all())
  const raw = rows.some((r) => r.name === name) ? (DEMO_PREDELETE[name] ?? []) : []
  return { items: classifyPredeleteItems(raw), raw }
}

/**
 * Step one of a delete: the backend says what references the store. The
 * dialog renders this response; skipping it is a regression (legacy doc §2).
 */
export function usePredelete(scope: Scope) {
  return useMutation<PredeleteImpact, Error, { name: string }>({
    mutationFn: async ({ name }) => {
      if (isDemoSession()) return demoImpact(scope, name)
      const res = await eventStoreApi.predelete({ ...scope, name })
      return { items: classifyPredeleteItems(res.items), raw: res.items }
    },
  })
}

/** Step two: the delete proper, echoing the predelete data (A1). */
export function useDeleteEventStore(scope: Scope) {
  const invalidate = useInvalidate(scope)
  return useMutation<void, Error, { name: string; predeleteData: unknown }>({
    mutationFn: async ({ name, predeleteData }) => {
      if (isDemoSession()) {
        storageStore(scope.clusterId, scope.scopeId).markDeleted([name])
        return
      }
      await eventStoreApi.remove({ ...scope, name, predeleteData })
    },
    onSuccess: invalidate,
  })
}

/** Upload a csv/xlsx as a named store (A2). Demo: the file becomes an upload row in the overlay. */
export function useUploadEventStore(scope: Scope) {
  const invalidate = useInvalidate(scope)
  return useMutation<void, Error, { name: string; file: File }>({
    mutationFn: async ({ name, file }) => {
      if (isDemoSession()) {
        const now = Date.now()
        const who = getSession()?.user
        const author = who?.name || 'demo'
        const userId = who?.email || 'demo@bloo.io'
        const ext = file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() ?? null
        storageStore(scope.clusterId, scope.scopeId).addArchive({
          name,
          author,
          createdBy: userId,
          userId,
          dataSource: `File Upload by ${author}`,
          sourceKind: 'upload',
          extension: ext,
          fileName: file.name.replace(/\.[^.]+$/, ''),
          createdAt: now,
          modifiedBy: userId,
          modifiedAt: now,
          scopeId: scope.scopeId,
        })
        return
      }
      await eventStoreApi.upload({ ...scope, name, file })
    },
    onSuccess: invalidate,
  })
}
